import React from "react";
import {Trans, useTranslation} from "@unibuddy/intl";
import {Button} from "@unibuddy/patron";
import styled, {createGlobalStyle} from "styled-components";

import Carousel from "../../../../General/components/Carousel/Carousel";
import MentorCarouselCard from "../MentorCarouselCard/MentorCarouselCard";
import MentorPlaceholder from "../../../../Mentor/components/MentorPlaceholder/MentorPlaceholder";
import {useBreakpoint} from "../../utils/useBreakpoint";
import {MentorField} from "../../../../../types/generated";
import {useUniversity} from "../../../../../widgets/modules/UniversityProvider/UniversityProvider";
import {
    INTL_HAPPY_CHATTING,
    INTL_SEE_MORE_DESCRIPTION,
    INTL_SEE_MORE_STUDENTS,
} from "../../../../../translations/mentorCard";

type SlideProps = {
    children: React.ReactElement;
};

type GetNumberOfSlidesProps = {
    isSmall: boolean;
    isMedium: boolean;
    isLarge: boolean;
};

export type MentorCarouselProps = {
    loading: boolean;
    mentors: MentorField[];
    onChatNow: (mentorId: string, cardPosition?: number) => void;
    onSeeMore?: () => void;
    showSeeMore?: boolean;
    isExternal?: boolean;
};

const StyledSlide = styled.div`
    width: 100%;
    height: 100%;
    padding: 20px 6px;
    display: flex !important;
    justify-content: space-around;
    > * {
        min-width: 0;
    }
    &:focus {
        outline: 0;
    }
`;

export const StyledCarouselContainer = styled.div`
    margin: 0 auto;
    max-width: 1280px;
`;

export const StyledLoadingCarouselContainer = styled(StyledCarouselContainer)`
    display: flex;
    justify-content: space-around;
`;

const StyledSeeMoreCard = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
    min-width: 270px;
    width: 100%;
    max-width: 320px;
    margin: 0 auto;
    min-height: 325px;
    border: 1px solid #e4e8eb;
    border-radius: 6px;
    color: ${({theme}) => theme.colors.universityColor};
    background-color: white;
    text-align: center;
    transition: transform ease 0.3s;
    z-index: 1;

    @media (min-width: 768px) {
        &:after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            background-color: white;
            border-radius: 4px;
            box-shadow: 0 4px 13px rgba(70, 70, 70, 0.1);
            z-index: -1;
            opacity: 0;
            transform: translate3d(0, -5px, 0);
            transition: opacity ease 0.3s, transform ease 0.3s;
        }

        &:hover {
            transform: translate3d(0, -5px, 0);
        }

        &:hover:after {
            opacity: 0.99;
            transform: none;
        }
    }
`;

const StyledSeeMoreDescription = styled.p`
    margin-top: 10px;
    padding: 0 25px;

    @media (min-width: 480px) {
        padding: 0 15px;
    }
`;

export const GlobalStyles = createGlobalStyle`
  .slick-arrow {
    width: 42px !important;
    height: 42px !important;
    margin-top: 10px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 50% !important;
    background-color: #ffffff !important;
    z-index: 2 !important;
  }

  @media (min-width: 480px) {
    .slick-arrow {
      width: 50px !important;
      height: 50px !important;
    }
  }

  .slick-arrow:hover {
    background-color: ${({theme}) => theme.colors.grey50} !important;
  }

  .slick-prev {
    left: -10px !important;
  }

  .slick-next {
    right: -10px !important;
  }

  .slick-dots {
    bottom: -35px !important;
  }

  .slick-dots li button:before {
    color: ${({theme}) => theme.colors.grey300} !important;
    font-size: 0.75rem !important;
  }

  .slick-dots li.slick-active button:before {
    color: ${({theme}) => theme.colors.universityColor} !important;
    opacity: 1 !important;
  }

  .slick-slider .slick-arrow-inner-stroke {
    stroke: ${({theme}) => theme.colors.universityColor} !important;
  }

  .slick-slide > div {
    width: 100% !important;
  }
`;

export const Slide = ({children}: SlideProps) => <StyledSlide>{children}</StyledSlide>;

export const getNumberOfSlides = ({isSmall, isMedium, isLarge}: GetNumberOfSlidesProps): number => {
    if (isLarge) return 3;
    if (isMedium || isSmall) return 2;
    return 1;
};

export function MentorCarousel({
    loading = true,
    mentors,
    onChatNow,
    onSeeMore,
    showSeeMore = true,
    isExternal = false,
}: MentorCarouselProps) {
    const {t} = useTranslation("mentorCard");
    const {isSmall, isMedium, isLarge} = useBreakpoint();
    const loadingItems = new Array(getNumberOfSlides({isSmall, isMedium, isLarge})).fill(1);
    const {features} = useUniversity();
    const isShowCountryFlagEnabled = features.get("showCountryFlag");

    if (loading) {
        return (
            <>
                <GlobalStyles />
                <StyledLoadingCarouselContainer>
                    {loadingItems.map((_, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Slide key={i}>
                            <MentorPlaceholder lines={3} />
                        </Slide>
                    ))}
                </StyledLoadingCarouselContainer>
            </>
        );
    }

    return (
        <>
            <GlobalStyles />
            <StyledCarouselContainer>
                <Carousel
                    dots={false}
                    slidesToScroll={1}
                    slidesToShow={getNumberOfSlides({
                        isSmall,
                        isMedium,
                        isLarge,
                    })}
                >
                    {mentors.map((mentor: MentorField, index: number) => (
                        <Slide key={mentor.id}>
                            <MentorCarouselCard
                                index={index}
                                onChatNow={onChatNow}
                                profilePhoto={mentor.profilePhoto}
                                mentorId={mentor.id}
                                firstName={mentor.firstName}
                                degreeName={mentor?.degree?.name ?? ""}
                                degreeLevel={mentor?.degreeLevel ?? ""}
                                placeLabel={mentor?.placeName ?? ""}
                                countryCode={mentor?.country?.code ?? ""}
                                primaryFlag={mentor?.mentorFields?.primaryFlag?.code ?? ""}
                                secondaryFlag={mentor?.mentorFields?.secondaryFlag?.code ?? ""}
                                languages={mentor?.languages ?? []}
                                year={mentor?.year ?? ""}
                                lastSeen={mentor.lastSeen ?? null}
                                isExternal={isExternal}
                                isShowCountryFlagEnabled={isShowCountryFlagEnabled}
                                style={isSmall && !isMedium && !isLarge && {maxWidth: "300px"}}
                            />
                        </Slide>
                    ))}
                    {showSeeMore ? (
                        <Slide>
                            <StyledSeeMoreCard>
                                <Button size="lg" onClick={onSeeMore} round color="university">
                                    {t(
                                        INTL_SEE_MORE_STUDENTS.key,
                                        INTL_SEE_MORE_STUDENTS.defaultValue,
                                    )}
                                </Button>
                                <StyledSeeMoreDescription>
                                    <Trans t={t} i18nKey={INTL_SEE_MORE_DESCRIPTION.key}>
                                        Read their profiles <br />
                                        Ask them anything about their experience <br />
                                    </Trans>
                                    {t(INTL_HAPPY_CHATTING.key, INTL_HAPPY_CHATTING.defaultValue)}
                                </StyledSeeMoreDescription>
                            </StyledSeeMoreCard>
                        </Slide>
                    ) : null}
                </Carousel>
            </StyledCarouselContainer>
        </>
    );
}
